/** The Clients list's address (its filters live in the URL) and the client page's tabs. */
import { z } from 'zod'
import type { ClientsQueryInput } from '@/core/clients'
import {
  billingMethods,
  clientSortKeys,
  locationCounts,
  paymentStanding,
  paymentTerms,
} from '@/core/clients'

export const clientsSearchSchema = z.object({
  q: z.string().optional(),
  billing: z.array(z.enum(billingMethods)).optional(),
  payment: z.array(z.enum(paymentStanding)).optional(),
  terms: z.array(z.enum(paymentTerms)).optional(),
  locations: z.array(z.enum(locationCounts)).optional(),
  sort: z.enum(clientSortKeys).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  page: z.number().int().min(1).optional(),
  size: z.number().int().optional(),
  view: z.enum(['deleted']).optional(),
})
export type ClientsSearch = z.infer<typeof clientsSearchSchema>

export const CLIENT_PAGE_SIZES = [25, 50, 100] as const

export function clientsQuery(search: ClientsSearch): ClientsQueryInput {
  return {
    q: search.q ?? '',
    billing: search.billing ?? [],
    payment: search.payment ?? [],
    terms: search.terms ?? [],
    locations: search.locations ?? [],
    sort: search.sort ? { key: search.sort, dir: search.dir ?? 'asc' } : null,
    page: (search.page ?? 1) - 1,
    pageSize: search.size ?? CLIENT_PAGE_SIZES[0],
  }
}

export function clientsFiltered(search: ClientsSearch): boolean {
  return Boolean(
    search.q ||
      [search.billing, search.payment, search.terms, search.locations].some(
        (l) => l && l.length > 0,
      ),
  )
}

/** The client page: Overview, Jobs, Invoices, Messages, or one location's tab. */
export const clientPageSearchSchema = z.object({
  tab: z.string().optional(),
  // From an invoice with nobody to send it to: open Add contact as a billing
  // contact, and go back to that invoice once one is added.
  add: z.literal('billing').optional(),
  invoice: z.string().optional(),
})

export type ClientTab =
  | { kind: 'overview' }
  | { kind: 'jobs' }
  | { kind: 'invoices' }
  | { kind: 'location'; id: string }

export function tabFrom(value: string | undefined): ClientTab {
  if (value === 'jobs' || value === 'invoices') return { kind: value }
  if (value?.startsWith('location-'))
    return { kind: 'location', id: value.slice('location-'.length) }
  return { kind: 'overview' }
}

export function tabValue(tab: ClientTab): string {
  return tab.kind === 'location' ? `location-${tab.id}` : tab.kind
}

/**
 * A location's name without its client's in front ("137 Wooster Store" on
 * Another Tomorrow's page): the client page's tabs and location cards.
 */
export function placeName(locationName: string, clientName: string): string {
  return locationName.startsWith(`${clientName} - `)
    ? locationName.slice(clientName.length + 3)
    : locationName
}
